import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import Modal from "../components/atoms/modal";
import GetHeroIcon from "../utils/getHeroIcon";
import SimpleDialogModal from "../components/molecules/modal-children/simpleDialogModal";
import ErrorParser from "../utils/errorParser";

import {
  useLazyGetLoginInfoQuery,
  useLazyGetLatestTosInfoQuery,
  useLazyGetProfileByEmailQuery,
  useLazyGetProfileByIdInQuery,
  useLazyGetUserInfoByUserIdQuery,
  useLazyGetUserInfoQuery,
  useLazyGetUserProfileQuery,
  useLazySendLoginActivityQuery,
  useLazyGetUserTosInfoQuery
} from "../store/slices/api/userInfoApiSlice";
import { useLazyGetCommonConfigQuery } from "@lxp-app/store/slices/api/commonConfigApi";
import { UserInfoSliceActions } from "@lxp-app/store/slices/userInfoSlice";
import { appLoaderActions } from "@lxp-app/store/slices/appLoaderSlice";
import { useLazyFetchRoleByUserIdQuery } from "@lxp-app/store/slices/api/rbacApi";
import { useLazyGetProfileForSupervisorQuery } from "@lxp-app/pages/admin-dashboard/slices/api/adminDashboardApiSlice";
import { AppMetaDataSliceActions } from "@lxp-app/store/slices/appMetaDataSlice";
import { useLazyGetUserIPQuery } from "@lxp-app/components/molecules/analytics/xapi";
import { xapiActions } from "@lxp-app/components/molecules/analytics/xapiSlice";
import { useLazyFetchAllRoleQuery } from "@lxp-app/store/slices/api/roleApi";
import { DarkmodeAction } from "@lxp-app/store/slices/darkmodeSlice";
import { RootState } from "@lxp-app/store/store";
import { normalizeRbacActiveRoles } from "@lxp-app/utils/roles";

const AuthProviderWrapper = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();
  const [logoutPopUpOpen, setLogoutPopUpOpen] = useState<boolean>(false);
  const [showContinueButton, setShowContinueButton] = useState<boolean>(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [timeoutHours, setTimeoutHours] = useState<number>();

  const { profile } = useSelector((state: RootState) => state.UserInfoSlice);
  const tenantConfig = useSelector(
    (state: RootState) => state.AppMetaDataSlice.tenantConfig
  );

  const [getUserIp] = useLazyGetUserIPQuery(undefined);
  const [getUserInfo] = useLazyGetUserInfoQuery();
  // const [getLanguage] = useLazyGetLanguageQuery();
  // const [getSourceFilter] = useLazyGetSourceFilterQuery();
  const [getUserProfile] = useLazyGetUserProfileQuery();
  const [getProfileByEmail] = useLazyGetProfileByEmailQuery();
  const [getProfileByIdIn] = useLazyGetProfileByIdInQuery();
  const [getProfileByUserId] = useLazyGetUserInfoByUserIdQuery();
  const [fetchRoleByUserId] = useLazyFetchRoleByUserIdQuery();
  const [fetchReportees] = useLazyGetProfileForSupervisorQuery();
  const [getLoginInfo] = useLazyGetLoginInfoQuery();
  const [sendLoginActivity] = useLazySendLoginActivityQuery();
  const [getLatestTos] = useLazyGetLatestTosInfoQuery();
  const [getUserTos] = useLazyGetUserTosInfoQuery();
  const [getRoles] = useLazyFetchAllRoleQuery();
  const [getCommonConfig] = useLazyGetCommonConfigQuery();

  // const { reset } = useIdleTimer({
  //   onActive,
  //   onPrompt,
  //   promptTimeout: timeoutHours ?? 30 * 60 * 1000,
  //   timeout: timeoutHours ?? 30 * 60 * 1000,
  //   onIdle: handleOnIdle,
  // });

  const handleLogoutPopUp = async () => {
    setLogoutPopUpOpen(!logoutPopUpOpen);
  };

  const getUserDetails = useCallback(async () => {
    try {
      dispatch(appLoaderActions.setIsLoading(true));
      const res = await keycloak.loadUserProfile();
      const ud = await getUserInfo({ email: res.email, kid: res.id });

      const errorData = ud?.error as any;

      if (errorData?.data?.error?.[0].split("_")[0] === "102-101-10101001") {
        sessionStorage.setItem("onboarding", "true");
        dispatch(appLoaderActions.setIsLoading(false));
        navigate("/error/user-not-found");
        dispatch(UserInfoSliceActions.setUser({ unAuthorized: true }));
      } else {
        dispatch(UserInfoSliceActions.setOrg(ud?.data?.data?.[0]?.org));
        dispatch(
          UserInfoSliceActions.setUser({
            ...profile,
            id: ud?.data?.data?.[0]?.id,
            email: ud?.data?.data[0]?.email
          })
        );

        if (ud.status === "rejected") throw ud.error;

        const [
          // utos, vtos,
          profileByEmail
        ] = await Promise.all([
          // await getUserTos({ uid: ud?.data?.data[0]?.id }),
          // await getLatestTos({ uid: ud?.data?.data[0]?.id }),
          await getProfileByEmail({
            email: ud?.data?.data[0]?.email,
            uid: ud?.data?.data[0]?.id
          })
        ]);

        const profileByIdIn = await getProfileByIdIn({
          userId: [profileByEmail?.data?.getProfileByEmail?.data?.id]
        });

        const profileByUserId = await getProfileByUserId(
          profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.id
        );
        const getRoleByUserId = !assessmentRoute.current
          ? await fetchRoleByUserId(
              profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.id
            )
          : null;

        const superAdmin = !assessmentRoute.current
          ? await getCommonConfig("superadmin")
          : null;

        // if (utos.status === "rejected") throw utos.error;
        // if (vtos.status === "rejected") throw vtos.error;
        if (profileByEmail.status === "rejected") throw profileByEmail.error;

        // if (utos.data.data.tosId === vtos.data.data.id) {
        //   dispatch(UserInfoSliceActions.updateTOS());
        //   dispatch(
        //     UserInfoSliceActions.updateTOSWithData({
        //       tosVersionId: utos.data.data.tosId,
        //       tosAcceptanceDate: utos.data.data.acceptedAt,
        //     }),
        //   );
        // }

        const externalAssessmentOrg =
          await getCommonConfig("assessmentRecOrgs").unwrap();

        const temp = {
          ...profileByUserId?.data?.getUserInfoByUserId?.data
        };

        const temp2 = {
          ...profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]
        };

        const userOrg =
          profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.org;

        const userId =
          profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.id;

        const supervisor =
          profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]
            ?.supervisor[0];

        const reportees = !assessmentRoute.current
          ? await fetchReportees(
              profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.id
            )
          : null;

        const activeRoles = !assessmentRoute.current
          ? normalizeRbacActiveRoles(
              getRoleByUserId?.data?.data?.activeRoles as string[] | undefined
            )
          : [];
        const roles = activeRoles;

        const superAdminArr = !assessmentRoute.current
          ? [...superAdmin?.data?.data?.value?.superadminrole]
          : [];

        const isSuperAdmin = superAdminArr.some((superAdminRole: string) =>
          roles.some((r) => r === superAdminRole)
        );

        const isManager: boolean = !assessmentRoute.current
          ? (profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.employment?.band.match(
              /Band (\w+)/i
            )?.length > 0 &&
              profileByIdIn?.data?.getProfileByIdIn?.data?.profiles?.[0]?.employment?.band.match(
                /Band (\w+)/i
              )[1] >= 2) ||
            reportees?.data?.getProfileForSupervisor?.data?.totalCount > 0
            ? true
            : false
          : false;

        const isExternalAssessmentOrg =
          externalAssessmentOrg?.data?.value?.orgs.some(
            (el: string) => el === userOrg
          );

        delete temp.id;
        delete temp.userId;
        delete temp2.id;
        delete temp2.supervisor;

        const user = Object.assign(
          {},
          temp,
          temp2,
          { id: userId },
          { supervisor: supervisor },
          { role: activeRoles },
          { isManager: isManager },
          { unAuthorized: false },
          { isSuperAdmin: isSuperAdmin },
          { hasAssessmentRecOrgs: isExternalAssessmentOrg },
          {
            isHitachiOrg:
              userOrg === "hitachi-academy" ||
              (userOrg === "gl-japan" &&
                ["dev", "development", "staging"].includes(
                  import.meta.env.MODE
                ))
          },
          {
            isTMobileOrg: userOrg === "t-mobile"
          },
          {
            reportees:
              reportees?.data.getProfileForSupervisor?.data?.myReportees || []
          }
        );

        if (user) {
          if (user.isHitachiOrg || user.isTMobileOrg) {
            document.documentElement.classList.add(`org-${user.org}`);
          }

          const isDarkMode =
            user?.preferences?.platformPreferences?.darkMode || false;
          dispatch(UserInfoSliceActions.setUser(user));
          dispatch(appLoaderActions.setIsLoading(false));
          dispatch(DarkmodeAction.setDarkMode(isDarkMode));
        }
        const firstTimeLoginDisplayed = sessionStorage.getItem(
          "firstTimeLoginDisplayed"
        );
        if (
          firstTimeLoginDisplayed == null ||
          firstTimeLoginDisplayed == "false"
        )
          getLoginInfo(undefined).then((res: any) => {
            if (!res.data?.data?.userId) {
              dispatch(UserInfoSliceActions.setFirstTimeLogin(true));
            } else {
              sendLoginActivity("login").then((res: any) => {
                dispatch(UserInfoSliceActions.setFirstTimeLogin(false));
              });
            }
            sessionStorage.setItem("firstTimeLoginDisplayed", "true");
            sessionStorage.setItem("onboarding", "true");
          });
      }
    } catch (err: any) {
      console.log({ err });

      ErrorParser(err, navigate);
      dispatch(appLoaderActions.setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    getLatestTos,
    getUserInfo,
    getUserProfile,
    getUserTos,
    keycloak
  ]);

  const fetchLanguageData = async () => {
    try {
      const languageData = await getCommonConfig("supportedLanguages");
      dispatch(
        AppMetaDataSliceActions.setLanguageData(languageData?.data?.data || [])
      );
    } catch (error) {
      console.log(error);
    }
  };
  const fetchSourceFilter = async () => {
    try {
      const sourceFilterData = await getCommonConfig("sourceFilter");
      dispatch(
        AppMetaDataSliceActions.setSourceFilterData(
          sourceFilterData?.data?.data || []
        )
      );
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUrlFetchInterval = async () => {
    try {
      const refetchInterval = await getCommonConfig("videoUrlRefetch");
      dispatch(
        AppMetaDataSliceActions.setRefetchInterval(
          refetchInterval?.data?.data?.value?.refetchIntervalInMin || 0
        )
      );
    } catch (error) {
      console.log(error);
    }
  };

  const fetchRoles = async () => {
    try {
      const roles = await getRoles(undefined);
      dispatch(AppMetaDataSliceActions.setRoles(roles?.data?.data || []));
    } catch (error) {
      console.log(error);
    }
  };

  const orgRouteApi = async () => {
    try {
      const res: any = await getCommonConfig("orgRouting");
      dispatch(UserInfoSliceActions.setOrgRoute(res?.data?.data?.value || {}));
    } catch (error) {
      console.log(error);
    }
  };

  const getAgendaBufferTime = async () => {
    try {
      const bufferData = await getCommonConfig(
        "agendaBufferDurationForUpdatesInHour"
      );
      if (bufferData?.data?.data?.value?.hours)
        dispatch(
          AppMetaDataSliceActions.setAgendaBufferTime(
            bufferData?.data?.data?.value?.hours
          )
        );
    } catch (error) {
      console.log(error);
    }
  };

  const getEventCancellationBuffer = async () => {
    try {
      const bufferData = await getCommonConfig(
        "eventBufferDurationForCancellationsInHour"
      );
      if (bufferData?.data?.data?.value?.hours)
        dispatch(
          AppMetaDataSliceActions.setEventCancellationBuffer(
            bufferData?.data?.data?.value?.hours
          )
        );
    } catch (error) {
      console.log(error);
    }
  };

  const getUserContext = async () => {
    try {
      const userIP: any = await getUserIp(undefined);
      if (userIP) {
        dispatch(xapiActions.setContext(userIP));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getPollingLimit = async () => {
    try {
      const pollingLimit = await getCommonConfig("pollingLimitInMinutes");
      if (pollingLimit?.data?.data?.value?.value)
        dispatch(
          AppMetaDataSliceActions.setPollingLimit(
            (pollingLimit?.data?.data?.value?.value * 60) / 5
          )
        );
    } catch (error) {
      console.log(error);
    }
  };

  const getIdleTimeOutLimit = async () => {
    try {
      const idleTimeout = await getCommonConfig("idleTimeout");
      // if (idleTimeout?.data?.data?.value?.hours)
      // setTimeoutHours(idleTimeout?.data?.data?.value?.hours * 60 * 60 * 1000);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchTenantConfig = async () => {
    try {
      const tenantConfig = await getCommonConfig("tenantConfig");
      dispatch(
        AppMetaDataSliceActions.setTenantConfig(
          tenantConfig?.data?.data?.value || {}
        )
      );
    } catch (error) {
      console.log(error);
    }
  };

  const onTokenExpired = () => {
    keycloak
      .updateToken(60)
      .then((refreshed) => {
        if (refreshed) {
          //changed keycloak.token to refreshToken
          dispatch(UserInfoSliceActions.setToken(keycloak.token));
        }
      })
      .catch(() => {
        //dialog box
        setShowContinueButton(false);
        // setLogoutPopUpOpen(true);
        if (timerRef.current) {
          timerRef.current = setTimeout(() => {
            logout();
          }, 10 * 1000);
        }
      });
  };

  const logout = () => {
    sendLoginActivity("logout").then((res: any) => {
      dispatch(UserInfoSliceActions.setFirstTimeLogin(false));
      keycloak.logout({
        redirectUri:
          window.location.origin +
          `/logout?org=${profile?.isHitachiOrg ? "hitachi-academy" : profile?.isTMobileOrg ? "t-mobile" : ""}`
      });
    });
    sessionStorage.clear();
    dispatch(UserInfoSliceActions.clearToken());
  };

  function onActive(): void {
    setLogoutPopUpOpen(false);
  }

  function handleOnIdle(): void {
    setLogoutPopUpOpen(false);
    // logout();
  }

  function onPrompt(): void {
    setLogoutPopUpOpen(true);
  }

  function handleContinue(): void {
    setLogoutPopUpOpen(false);
    // reset();
  }

  const checkTosAcceptance = async (uid: string) => {
    const [utos, vtos] = await Promise.all([
      await getUserTos({ uid: uid }),
      await getLatestTos({ uid: uid })
    ]);
    if (utos.data.data.tosId === vtos.data.data.id) {
      dispatch(UserInfoSliceActions.updateTOS());
      dispatch(
        UserInfoSliceActions.updateTOSWithData({
          tosVersionId: utos.data.data.tosId,
          tosAcceptanceDate: utos.data.data.acceptedAt
        })
      );
    } else
      navigate(`/tos`, {
        replace: true
      });
  };

  const location = useLocation();
  const assessmentRoute = useRef(false);
  const previousLocationRef = useRef(location);

  useEffect(() => {
    if (location.pathname.startsWith("/assessment/feedback")) {
      assessmentRoute.current = false;
    } else if (location.pathname.startsWith("/assessment"))
      assessmentRoute.current = true;
    else {
      assessmentRoute.current = false;
    }

    if (
      previousLocationRef.current.pathname.startsWith("/assessment") &&
      (location.pathname.startsWith("/assessment/feedback") ||
        !location.pathname.startsWith("/assessment"))
    ) {
      if (keycloak.authenticated) {
        dispatch(UserInfoSliceActions.setToken(keycloak.token));
        // orgRouteApi();
        getUserDetails();
        // orgRouteApi();
        // fetchLanguageData();
        // fetchSourceFilter();
        // getAgendaBufferTime();
        // getEventCancellationBuffer();
        // getUserContext();
        // getPollingLimit();
        // getIdleTimeOutLimit();
        // fetchRoles();
      }
    }
    previousLocationRef.current = location;
  }, [window.location.pathname, keycloak.authenticated]);

  useEffect(() => {
    if (keycloak.authenticated) {
      dispatch(UserInfoSliceActions.setToken(keycloak.token));
      // orgRouteApi();
      getUserDetails();
    }
  }, [getUserDetails, keycloak.authenticated]);

  useEffect(() => {
    if (keycloak.authenticated && profile?.id != undefined && profile?.role) {
      orgRouteApi();
      fetchTenantConfig();
      fetchLanguageData();
      fetchSourceFilter();
      // if (!assessmentRoute.current) {
      getAgendaBufferTime();
      getEventCancellationBuffer();
      getUserContext();
      getPollingLimit();
      getIdleTimeOutLimit();
      fetchRoles();
      fetchUrlFetchInterval();
      // }
    }
  }, [profile?.id && profile?.role]);

  useEffect(() => {
    const showTos = tenantConfig?.tos?.enable || false;
    if (showTos && profile?.id) {
      checkTosAcceptance(profile?.id!);
    }
  }, [tenantConfig, profile?.id]);

  useEffect(() => {
    keycloak.onTokenExpired = onTokenExpired;
  }, [keycloak, dispatch]);

  // useEffect(() => {
  //   getIdleTimeOutLimit();
  // }, []);
  return (
    <>
      <>{children}</>
      <Modal
        open={logoutPopUpOpen}
        setOpen={setLogoutPopUpOpen}
        // width={true}
        dialogueClassName="sm:max-w-3xl"
        closeBtn={true}
      >
        <SimpleDialogModal
          isShow={logoutPopUpOpen}
          onConfirm={() => {
            logout();
          }}
          icon={
            <GetHeroIcon
              name="ExclamationTriangleIcon"
              className="w-5 h-5"
              variant="outline"
            />
          }
          showContinueButton={showContinueButton}
          setOpen={handleLogoutPopUp}
          onContinue={handleContinue}
          buttonName="Continue "
          heading="Alert"
          message={`You have been idle for the past 3 hours. Click 'Continue' to stay logged in or 'Logout' to exit.`}
        />
      </Modal>
    </>
  );
};

export default AuthProviderWrapper;
